import { Alert } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import OrganizationSelect from "../OrganizationSelect";
import { Button } from "../../../common/Button";
import { useSchedulingOrg } from "../../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { useAppointmentsQuery } from "../../../../hooks/ReactQueryHooks/useAppointmentsQuery";
import { useServiceCategoriesQuery } from "../../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { buildCalendarEvents } from "../calendar/eventModel";
import SchedulingCalendar from "../calendar/SchedulingCalendar";
import AppointmentsList from "./AppointmentsList";
import AppointmentDetailModal from "./AppointmentDetailModal";
import { useSchedulingTimeZone } from "../../../../hooks/useSchedulingTimeZone";
import TimeZoneSelect from "../TimeZoneSelect";
import "../Scheduling.css";

const AppointmentsApp = ({ dashboard = false, practitionerRef, organizationRef: orgOverride, readOnly = false }) => {
  const orgContext = useSchedulingOrg();
  const {
    organizationRef: sessionOrg,
    practitionerRef: selfRef,
    organizationTimeZone,
    user,
  } = orgContext;
  const displayZone = useSchedulingTimeZone(user);
  const organizationRef = orgOverride || sessionOrg;
  const [view, setView] = useState(dashboard ? "list" : "calendar");
  const [range, setRange] = useState({ startsAt: null, endsAt: null });
  const [selected, setSelected] = useState(null);
  useEffect(() => { setSelected(null); }, [organizationRef]);

  const { data: appointments = [], refetch, isError, isLoading, error } = useAppointmentsQuery({
    organizationRef,
    practitionerRef: practitionerRef || (dashboard ? selfRef : undefined),
    startsAt: view === "calendar" ? range.startsAt : undefined,
    endsAt: view === "calendar" ? range.endsAt : undefined,
    enabled: view === "calendar" && !!range.startsAt && !!range.endsAt,

  });
  const { data: categories = [] } = useServiceCategoriesQuery({ organizationRef, active: undefined });

  const events = useMemo(
    () =>
      buildCalendarEvents({
        appointments,
        categories,
        showBookings: true,
      }),
    [appointments, categories]
  );

  return (
    <section className="common-listing">
      {view === "calendar" && isError && <Alert severity="error" action={<button onClick={() => refetch()}>Retry</button>}>{error?.message || "Could not load appointments."}</Alert>}
      {view === "calendar" && isLoading && <p role="status">Loading appointments…</p>}
      <div className="heading-wrap h-change scheduling-appointments-toolbar">
        <div className="scheduling-appointments-title">
          <h1>Appointments</h1>
          {!orgOverride && !dashboard && <OrganizationSelect context={orgContext} inputId="appointments-org" />}
        </div>
        <div className="right-wrap">
          <Button variant={view === "calendar" ? "primary" : "secondary"} onClick={() => setView("calendar")}>
            Calendar
          </Button>
          <Button variant={view === "list" ? "primary" : "secondary"} onClick={() => setView("list")}>
            List
          </Button>
        </div>
      </div>
      {view === "calendar" ? (
        <SchedulingCalendar
          timeZone={displayZone.timeZone}
          timeZoneControl={<TimeZoneSelect value={displayZone.preference} onChange={displayZone.setPreference}
            localZone={displayZone.deviceTimeZone} organizationZone={organizationTimeZone} />}
          events={isLoading || isError ? [] : events}
          extraViews="timeGridWeek,timeGridDay,listWeek"
          onDatesSet={({ start, end }) =>
            setRange({ startsAt: start.toISOString(), endsAt: end.toISOString() })
          }
          onEventClick={(_event, props) => {
            if (props.kind === "appointment") setSelected(props.source);
          }}
        />
      ) : (
        <AppointmentsList
          organizationRef={organizationRef}
          practitionerRef={practitionerRef || (dashboard ? selfRef : undefined)}
          readOnly={readOnly}
          dashboard={dashboard}
        />
      )}
      {selected ? (
        <AppointmentDetailModal
          timezoneName={displayZone.timeZone}
          appointment={selected}
          organizationRef={organizationRef}
          readOnly={readOnly}
          onHide={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </section>
  );
};

export default AppointmentsApp;
